import { ChangeDetectorRef, Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  SelectionDropdownInfo,
  SelectionOption,
} from 'src/app/modules/campaign/modules/campaign-shared-module/types/selection';
import { ModalInfo } from 'src/app/shared/models';
import { BrowserInfoService } from '../../../shared/services';
import { BrowserInfoData } from 'src/app/shared/models/user/browserInfo';
import { CampaignService } from 'src/app/shared/services/requests/campaign.service';
import { catchError, map, of, Subject, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { GeoCodeInfo } from 'src/app/shared/models/campaign/campaign-settings';
import { emailPattern } from 'src/app/shared/utilities';
import { patchState } from 'src/app/shared/utilities/object-patch';
import { LinkedinAddAccountRequest } from 'src/app/shared/models/user/linkedinAccount';
import {
  LdAddingAccountStatus,
  LdAddingModalState,
  LdCodeVerificationType,
} from 'src/app/shared/models/campaign/campaign-ld-account';
import { BgPathsForLdAccounts } from 'src/app/shared/enums/bg-paths-ld-accounts.enum';
import { NotificationTypeEnum } from 'src/app/shared/enums';
import { NotificationService } from 'src/app/shared/services';

export interface AddLinkedinAccountModalData {
  extensionInstalled: boolean;
  login?: string;
}

@Component({
  selector: 'app-add-linkedin-account-modal',
  templateUrl: './add-linkedin-account-modal.component.html',
  styleUrls: ['./add-linkedin-account-modal.component.scss'],
  standalone: false,
})
export class AddLinkedinAccountModalComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddLinkedinAccountModalData,
    private dialogRef: MatDialogRef<AddLinkedinAccountModalComponent>,
    private browserInfoService: BrowserInfoService,
    private campaignService: CampaignService,
    private cdr: ChangeDetectorRef,
  ) {}

  private readonly STATUS_MODAL_WIDTH = 521;
  private readonly STATUS_MODAL_HEIGHT = 533;
  private readonly RESEND_CODE_COOLDOWN_MS = 30000;
  private readonly bgPathsEnum = BgPathsForLdAccounts;
  private browserData: BrowserInfoData;
  private sessionId = '';
  private isFirstWaitingStatus = true;
  private reconnectLogin: string | null = null;
  private unsubscribe$ = new Subject<void>();

  readonly INPUT_PIN_AMOUNT = 6;

  info: ModalInfo = {
    type: 'simple-modal',
    closeModalBtnId: 'close-btn-m',
    header: {
      title: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.TITLE',
    },
    actions: {
      cancel: {
        id: 'm-cancel-action-btn',
      },
      submit: {
        id: 'm-add-linkedin-modal',
        extraClass: 'bright',
        text: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.CONNECT',
        disabled: false,
      },
    },
  };

  selectionDropdownInfo: SelectionDropdownInfo = {
    noOptionsSearchText: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.NO_OPTIONS_COUNTRIES',
  };

  state: LdAddingModalState = 'settings';

  ldDataForm: FormGroup = new FormGroup({
    login: new FormControl('', [Validators.required, Validators.pattern(emailPattern)]),
    password: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
  });

  pinCodeForm: FormGroup = new FormGroup({
    pinCode: new FormControl('', [
      Validators.required,
      Validators.minLength(this.INPUT_PIN_AMOUNT),
    ]),
  });

  get password(): FormControl {
    return this.ldDataForm.get('password') as FormControl;
  }

  codeBtnDisabled = true;
  geoListSelection: SelectionOption[] = [];
  textForWaiting: string[] = [
    'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.INFO_TEXT',
    'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.PROXY_INFO_TEXT',
  ];
  textIndex: 0 | 1 = 1;

  codeVerificationType: LdCodeVerificationType = 'email';
  codeDescriptionKey = 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.STATUS_LABELS.CODE';
  resendCodeDisabled = false;
  tryAnotherWayDisabled = false;

  showPasswordEye = false;
  passwordEyeToggled = false;

  ngOnInit(): void {
    this.browserData = this.browserInfoService.getFingerprint();
    this.reconnectLogin = this.data.login ?? null;
    this.applyReconnectLogin();
    this.info = patchState(this.info, ['actions', 'submit'], {
      disabled: this.isSubmitBtnDisabled(),
    });
    this.setGeoListSelection();
    this.changeModalInfo();
    this.setSubmitBtnDisableBasedOnFormValidation();
    this.setCodeBtnDisabledBasedOnFormValidation();
    this.handlePasswordEye();
  }

  closeModal(): void {
    const needToReloadList = !(this.state === 'settings');

    this.dialogRef.close(needToReloadList);
  }

  onSubmit(): void {
    const isDataFormValid = this.isDataFormValid();
    this.info = patchState(this.info, ['actions', 'submit'], { disabled: true });
    if (isDataFormValid && this.browserData) {
      const accountRequestData: LinkedinAddAccountRequest = {
        userAgent: this.browserData.userAgent,
        vendor: this.browserData.vendor,
        renderer: this.browserData.renderer,
        login: this.ldDataForm.get('login').value,
        password: this.ldDataForm.get('password').value,
        geoCode: this.ldDataForm.get('country').value,
      };

      this.campaignService
        .addLdAccount(accountRequestData)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((accountValue: any) => {
          if (accountValue) {
            this.changeStateAndModalInfo('waiting');
            this.sessionId = accountValue.object;
            this.waitUntilAddingIsResolved(this.sessionId);
          }
        });
    }
  }

  changeModalInfo(): void {
    if (this.state === 'settings') {
      this.info = {
        ...this.info,
        closeModalBtnId: 'close-btn-m',
        hideCloseButton: false,
        header: {
          title: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.TITLE',
        },
        actions: {
          cancel: {
            id: 'm-cancel-action-btn',
          },
          submit: {
            id: 'm-add-linkedin-modal',
            extraClass: 'bright',
            text: 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.CONNECT',
            disabled: this.isSubmitBtnDisabled(),
          },
        },
        fixed: null,
        backgroundImage: null,
      };
    }
    if (this.state === 'waiting') {
      this.info = {
        ...this.info,
        hideCloseButton: true,
        actions: null,
        header: { text: null, title: null },
        fixed: {
          width: this.STATUS_MODAL_WIDTH,
          height: this.STATUS_MODAL_HEIGHT,
        },
        backgroundImage: this.bgPathsEnum.WAITING,
      };
    }
    if (this.state === 'in-app-approval') {
      this.info = {
        ...this.info,
        hideCloseButton: true,
        actions: null,
        header: { text: null, title: null },
        fixed: {
          width: this.STATUS_MODAL_WIDTH,
          height: this.STATUS_MODAL_HEIGHT,
        },
        backgroundImage: this.bgPathsEnum.WAITING,
      };
    }
    if (this.state === 'error' || this.state === 'unsupported-action') {
      this.info = {
        ...this.info,
        hideCloseButton: true,
        actions: {
          submit: null,
          cancel: null,
          custom: {
            content: true,
          },
        },
        header: { text: null, title: null },
        fixed: {
          width: this.STATUS_MODAL_WIDTH,
          height: this.STATUS_MODAL_HEIGHT,
        },
        backgroundImage: this.bgPathsEnum.ERROR,
      };
    }
    if (this.state === 'success') {
      this.info = {
        ...this.info,
        hideCloseButton: true,
        actions: {
          submit: null,
          cancel: null,
          custom: {
            content: true,
          },
        },
        header: { text: null, title: null },
        fixed: {
          width: this.STATUS_MODAL_WIDTH,
          height: this.STATUS_MODAL_HEIGHT,
        },
        backgroundImage: this.bgPathsEnum.SUCCESS,
      };
    }
    if (this.state === 'code' || this.state === 'code-invalid') {
      this.info = {
        ...this.info,
        hideCloseButton: true,
        actions: {
          submit: null,
          cancel: null,
          custom: {
            content: true,
          },
        },
        header: { text: null, title: null },
        fixed: {
          width: this.STATUS_MODAL_WIDTH,
          height: this.STATUS_MODAL_HEIGHT,
        },
        backgroundImage:
          this.state === 'code-invalid' ? this.bgPathsEnum.ERROR : this.bgPathsEnum.WAITING,
      };
    }
  }

  changeStateAndModalInfo(state: LdAddingModalState): void {
    this.state = state;
    if (this.state === 'settings') {
      this.isFirstWaitingStatus = true;
      this.ldDataForm.reset();
      this.applyReconnectLogin();
      this.setCodeVerificationType('email');
    }
    this.cdr.detectChanges();
    this.changeModalInfo();
  }

  sendCodeForLogin() {
    const pinCode = this.pinCodeForm.get('pinCode').value;

    if (this.pinCodeForm.valid && pinCode.length === this.INPUT_PIN_AMOUNT) {
      this.codeBtnDisabled = true;
      this.campaignService
        .addEmailCodeToLdAccount(this.sessionId, pinCode)
        .pipe(
          takeUntil(this.unsubscribe$),
          catchError((e) => {
            this.changeStateAndModalInfo('error');
            throw e;
          }),
        )
        .subscribe(() => {
          this.waitUntilAddingIsResolved(this.sessionId);
        });
    }
  }

  resendCode(): void {
    this.resendCodeDisabled = true;
    this.campaignService
      .resendLdAccountCode(this.sessionId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.notificationService.notify(
            NotificationTypeEnum.SUCCESS,
            'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.RESEND_CODE_SUCCESS',
          );
          this.startResendCodeCooldown();
        }),
        catchError(() => {
          this.resendCodeDisabled = false;
          this.notificationService.notify(
            NotificationTypeEnum.ERROR,
            'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.RESEND_CODE_ERROR',
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  tryAnotherWay(): void {
    this.tryAnotherWayDisabled = true;
    this.campaignService
      .tryAnotherLdAccountVerificationWay(this.sessionId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => this.waitUntilAddingIsResolved(this.sessionId)),
        catchError(() => {
          this.tryAnotherWayDisabled = false;
          this.notificationService.notify(
            NotificationTypeEnum.ERROR,
            'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.TRY_ANOTHER_WAY_ERROR',
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  private startResendCodeCooldown(): void {
    timer(this.RESEND_CODE_COOLDOWN_MS)
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.resendCodeDisabled = false;
      });
  }

  private handlePasswordEye(): void {
    this.password.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      const passwordExist: boolean = value?.length;

      if (this.showPasswordEye !== passwordExist) {
        this.showPasswordEye = passwordExist;

        if (!this.showPasswordEye) {
          this.passwordEyeToggled = false;
        }
      }
    });
  }

  private setSubmitBtnDisableBasedOnFormValidation(): void {
    this.ldDataForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.info = patchState(this.info, ['actions', 'submit'], {
        disabled: this.isSubmitBtnDisabled(),
      });
    });
  }

  private setCodeBtnDisabledBasedOnFormValidation(): void {
    this.pinCodeForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.codeBtnDisabled = this.isCodeBtnDisabled();
    });
  }

  private isSubmitBtnDisabled(): boolean {
    const formValues = this.ldDataForm.getRawValue();
    return !(
      formValues.login &&
      formValues.password &&
      formValues.country &&
      this.isDataFormValid()
    );
  }

  private applyReconnectLogin(): void {
    if (!this.reconnectLogin) return;

    this.ldDataForm.get('login').setValue(this.reconnectLogin);
    this.ldDataForm.get('login').disable();
  }

  private isCodeBtnDisabled(): boolean {
    const pinCode = this.pinCodeForm.get('pinCode').value;
    return !(pinCode && this.pinCodeForm.valid);
  }

  private setGeoListSelection(): void {
    this.campaignService
      .getGeoCodeList()
      .pipe(
        takeUntil(this.unsubscribe$),
        map((geoList: GeoCodeInfo[]): SelectionOption[] => {
          return geoList.map((item) => ({
            value: item.code,
            label: item.name,
          }));
        }),
      )
      .subscribe((geoListSelection: SelectionOption[]) => {
        if (geoListSelection) {
          this.geoListSelection = geoListSelection;
        }
      });
  }

  private waitUntilAddingIsResolved(sessionId: string): void {
    timer(2000)
      .pipe(
        take(1),
        takeUntil(this.unsubscribe$),
        switchMap(() => this.campaignService.checkLdAccountProcessingStatus(sessionId)),
        tap((accountValue: any) => {
          const state = accountValue.object;

          this.handleResponce(state, sessionId);
        }),
        catchError(() => {
          this.changeStateAndModalInfo('error');
          return of(null);
        }),
      )
      .subscribe();
  }

  private handleResponce(accountStatus: LdAddingAccountStatus, sessionId: string): void {
    const isStatusFailure = this.isStatusFailure(accountStatus);
    if (isStatusFailure || !accountStatus) {
      this.changeStateAndModalInfo('error');
      return;
    }

    if (accountStatus === 'PENDING') {
      this.changeStateAndModalInfo('waiting');
      this.waitUntilAddingIsResolved(sessionId);
      if (this.isFirstWaitingStatus) {
        this.isFirstWaitingStatus = false;

        this.textIndex = 1;

        timer(60000)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(() => {
            this.textIndex = 0;
          });
      }
      return;
    }

    if (accountStatus === 'SUCCESS') {
      this.changeStateAndModalInfo('success');
      return;
    }

    if (accountStatus === 'NEED_EMAIL_CODE') {
      this.setCodeVerificationType('email');
      this.changeStateAndModalInfo('code');
      return;
    }

    if (accountStatus === 'NEED_2FA_CODE') {
      this.setCodeVerificationType('2fa');
      this.changeStateAndModalInfo('code');
      return;
    }

    if (accountStatus === 'EMAIL_CODE_INVALID') {
      this.changeStateAndModalInfo('code-invalid');
      this.pinCodeForm.get('pinCode')?.setValue('');
      return;
    }

    if (accountStatus === 'NEED_IN_APP_APPROVAL') {
      this.changeStateAndModalInfo('in-app-approval');
      this.waitUntilAddingIsResolved(sessionId);
      return;
    }

    if (accountStatus === 'NEED_CAPTCHA' || accountStatus === 'NEED_PHONE_REGISTER') {
      // Backend detects these LinkedIn checkpoints but there is no submit-back contract yet
      // (captcha payload / phone number endpoint). Surface a clear dead-end instead of hanging
      // on the waiting screen until the backend contract is defined.
      this.changeStateAndModalInfo('unsupported-action');
      return;
    }
  }

  private setCodeVerificationType(type: LdCodeVerificationType): void {
    this.codeVerificationType = type;
    this.codeDescriptionKey =
      type === '2fa'
        ? 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.STATUS_LABELS.CODE_2FA'
        : 'CAMPAIGN.ACCOUNTS.LINKEDIN_ACCOUNTS.ADD_MODAL.STATUS_LABELS.CODE';
    this.resendCodeDisabled = false;
    this.tryAnotherWayDisabled = false;
  }

  private isDataFormValid(): boolean {
    return this.ldDataForm.valid;
  }

  private isStatusFailure(accountStatus: LdAddingAccountStatus): boolean {
    return (
      accountStatus === 'CAPTCHA_ERROR' ||
      accountStatus === 'FAILED' ||
      accountStatus === 'SESSION_NOT_FOUND' ||
      accountStatus === 'TIMEOUT_ERROR' ||
      accountStatus === 'INVALID_ACCOUNT' ||
      accountStatus === 'INVALID_CREDENTIALS'
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
